import { useEffect, useMemo, useRef, useState } from 'react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { IdentityCard } from '@/components/IdentityCard';
import { QrCodeView } from '@/components/QrCodeView';
import { QrScannerSheet } from '@/components/QrScannerSheet';
import { container } from '@/services/container';
import { groupMemberName } from '@/services/GroupService';
import { summarizeMessage, isRedpackMessage, parseRedpack } from '@/services/ChatService';
import { useNoticeStore } from '@/store/notices';
import { useChatStore } from '@/store/chat';
import { useGroupStore } from '@/store/group';
import { useIdentityStore } from '@/store/identity';
import { useNetworkStore } from '@/store/network';
import { useUiStore } from '@/store/ui';
import { MeshLinkError } from '@/lib/result';
import { shortId } from '@/lib/format';
import { buildPayUri } from '@/lib/qrPay';
import { useCopy } from '@/hooks/useCopy';
import { loadContactNotes, saveContactNote, resolveContactName } from '@/utils/contactNotes';
import { saveFavorite } from '@/utils/favorites';
import type { Message, ChatMedia } from '@shared/types';
import type { QrContact } from '@/lib/qrContact';
import { Component, type ReactNode } from 'react';

/** 群面板渲染错误边界：即使群数据异常也不整页白屏，降级提示并保留列表可用。 */
class GroupPanelBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { failed: false };
  }
  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }
  override render(): ReactNode {
    if (this.state.failed) {
      return (
        <div className="flex flex-1 items-center justify-center">
          <EmptyState title="群面板加载失败" description="群数据可能有误，请刷新页面重试" />
        </div>
      );
    }
    return this.props.children;
  }
}

/** 聊天媒体字节 → 临时 objectURL（组件卸载时撤销）。mime 优先取消息体元数据（网络回退时 getImage 无 mime，播放器需正确类型）。 */
function useChatMedia(cid: string | undefined, mime?: string): { url: string | null } {
  const [state, setState] = useState<{ url: string | null }>({ url: null });
  useEffect(() => {
    let active = true;
    let created: string | null = null;
    if (!cid || !container.hasPeerClient()) {
      setState({ url: null });
      return;
    }
    void container.feed
      .getImage(cid)
      .then((res) => {
        if (!active || !res) return;
        const type = mime || res.mime || 'application/octet-stream';
        created = URL.createObjectURL(new Blob([new Uint8Array(res.bytes)], { type }));
        setState({ url: created });
      })
      .catch(() => {
        if (active) setState({ url: null });
      });
    return () => {
      active = false;
      if (created) URL.revokeObjectURL(created);
    };
  }, [cid, mime]);
  return state;
}

/** 图片气泡：objectURL 渲染 + 点击放大（复用 Lightbox）。 */
function ChatImage({ media, onPreview }: { media: ChatMedia; onPreview: (cid: string) => void }): JSX.Element {
  const { url } = useChatMedia(media.cid);
  if (!url) return <div className="h-24 w-40 rounded-btn bg-panel-2" aria-hidden />;
  return (
    <img
      src={url}
      alt={media.name ?? ''}
      role="button"
      tabIndex={0}
      className="max-h-64 w-auto max-w-[240px] cursor-zoom-in rounded-btn object-contain transition hover:opacity-85"
      onClick={() => onPreview(media.cid)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onPreview(media.cid);
      }}
    />
  );
}

/** 语音气泡：audio 播放器 + 时长。 */
function ChatAudio({ media }: { media: ChatMedia }): JSX.Element {
  const { url } = useChatMedia(media.cid, media.mime);
  if (!url) return <div className="flex h-9 w-44 items-center gap-2 rounded-btn bg-panel-2 px-2 text-xs text-dim">加载语音…</div>;
  const dur = media.duration ? `${Math.round(media.duration / 1000)}"` : '';
  return (
    <div className="flex max-w-[240px] items-center gap-2">
      <audio controls preload="metadata" className="h-9 w-44" src={url} />
      {dur ? <span className="whitespace-nowrap text-[10px] text-dim">{dur}</span> : null}
    </div>
  );
}

/** 视频气泡：video 播放器 + 文件名/大小。 */
function ChatVideo({ media }: { media: ChatMedia }): JSX.Element {
  const { url } = useChatMedia(media.cid, media.mime);
  const size = media.size > 1024 * 1024 ? `${(media.size / (1024 * 1024)).toFixed(1)}MB` : `${Math.max(1, Math.round(media.size / 1024))}KB`;
  if (!url) return <div className="flex h-28 w-52 items-center justify-center rounded-btn bg-panel-2 text-xs text-dim">加载视频…</div>;
  return (
    <div className="max-w-[260px]">
      <video controls preload="metadata" className="max-h-56 w-full rounded-btn bg-black/40" src={url} />
      <p className="mt-0.5 truncate text-[10px] text-dim">
        {media.name || '视频'} · {size}
      </p>
    </div>
  );
}

/** 文件气泡：文件名 + 大小 + 下载。 */
function ChatFile({ media }: { media: ChatMedia }): JSX.Element {
  const { url } = useChatMedia(media.cid, media.mime);
  const size = media.size > 1024 * 1024 ? `${(media.size / (1024 * 1024)).toFixed(1)}MB` : `${Math.max(1, Math.round(media.size / 1024))}KB`;
  return (
    <div className="max-w-[240px]">
      {url ? (
        <a
          href={url}
          download={media.name ?? '文件'}
          className="flex items-center gap-2 rounded-btn border border-line bg-panel/60 px-2 py-1.5 text-xs hover:bg-panel-2"
        >
          <span className="text-base" aria-hidden>
            📄
          </span>
          <span className="min-w-0">
            <span className="block truncate text-text">{media.name || '文件'}</span>
            <span className="block text-[10px] text-dim">{size} · 点击下载</span>
          </span>
        </a>
      ) : (
        <div className="flex items-center gap-2 rounded-btn border border-line bg-panel/60 px-2 py-1.5 text-xs text-dim">
          <span className="text-base" aria-hidden>
            📄
          </span>
          {media.name || '文件'} · {size}
        </div>
      )}
    </div>
  );
}

/** 消息气泡：出站右对齐，入站左对齐；群消息可带发送者名。v3.11 支持媒体与引用。v3.13 长按/右键唤起操作菜单（保存/分享/收藏）。 */
function Bubble({
  msg,
  senderName,
  allMsgs,
  onQuote,
  onAction,
}: {
  msg: Message;
  senderName?: string;
  allMsgs: Message[];
  onQuote: (m: Message) => void;
  onAction: (m: Message, x: number, y: number) => void;
}): JSX.Element {
  const out = msg.dir === 'out';
  const meta = msg.meta as { fromMailbox?: boolean } | undefined;
  const pressTimer = useRef<number | null>(null);
  const firedRef = useRef(false);
  const startPress = (e: React.PointerEvent): void => {
    firedRef.current = false;
    if (pressTimer.current) window.clearTimeout(pressTimer.current);
    pressTimer.current = window.setTimeout(() => {
      firedRef.current = true;
      onAction(msg, e.clientX, e.clientY);
    }, 500);
  };
  const clearPress = (): void => {
    if (pressTimer.current) {
      window.clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
  };
  const statusText: Record<Message['status'], string> = {
    sending: '◷',
    sent: '✓',
    delivered: '✓✓',
    read: '✓✓',
    failed: '!',
  };
  const quoted = msg.replyTo ? allMsgs.find((m) => m.msgId === msg.replyTo) ?? null : null;
  return (
    <div
      className={`group flex ${out ? 'justify-end' : 'justify-start'}`}
      onPointerDown={startPress}
      onPointerUp={clearPress}
      onPointerLeave={clearPress}
      onPointerCancel={clearPress}
      onContextMenu={(e) => {
        e.preventDefault();
        onAction(msg, e.clientX, e.clientY);
      }}
      onClickCapture={(e) => {
        if (firedRef.current) {
          e.preventDefault();
          e.stopPropagation();
          firedRef.current = false;
        }
      }}
    >
      <div
        className={`max-w-[78%] rounded-card px-3 py-2 text-sm ${
          out ? 'bg-accent-dim text-text' : 'bg-panel-2 text-text'
        }`}
      >
        {!out && senderName ? (
          <p className="mb-0.5 text-[10px] text-accent">{senderName}</p>
        ) : null}
        {quoted ? (
          <div className="mb-1 rounded border-l-2 border-accent/40 bg-panel/60 px-2 py-1 text-[11px] text-dim">
            <span className="mr-1">
              {out ? '我' : quoted.dir === 'in' ? (senderName ?? '对方') : '对方'}
            </span>
            {summarizeMessage(quoted)}
          </div>
        ) : null}
        {msg.kind === 'image' && msg.media ? <ChatImage media={msg.media} onPreview={() => useUiStore.getState().openImageViewer(msg.media!.cid)} /> : null}
        {msg.kind === 'video' && msg.media ? <ChatVideo media={msg.media} /> : null}
        {msg.kind === 'audio' && msg.media ? <ChatAudio media={msg.media} /> : null}
        {msg.kind === 'file' && msg.media ? <ChatFile media={msg.media} /> : null}
        {msg.kind === 'text' ? (
          isRedpackMessage(msg.body) ? (
            <div className="flex items-center gap-2 rounded-lg border border-accent/30 bg-panel/60 px-3 py-2">
              <span className="text-2xl" aria-hidden>
                🧧
              </span>
              <div>
                <p className="text-xs font-medium text-text">{msg.body ? (parseRedpack(msg.body)?.note ?? '恭喜发财') : '恭喜发财'}</p>
                <p className="text-[11px] text-dim">
                  {msg.body ? (parseRedpack(msg.body)?.amountDpb ?? '') : ''} DPB · 已上链转账
                  {msg.body && parseRedpack(msg.body)?.txHash ? ` · ${(parseRedpack(msg.body)?.txHash ?? '').slice(0, 10)}…` : ''}
                </p>
              </div>
            </div>
          ) : (
            <p className="whitespace-pre-wrap break-words">{msg.body || '（无法解密的消息）'}</p>
          )
        ) : null}
        <div className="mt-1 flex items-center justify-end gap-2 text-[10px] text-dim">
          <button
            type="button"
            title="引用回复"
            className="rounded px-1 opacity-0 transition group-hover:opacity-100 focus:opacity-100"
            onClick={() => onQuote(msg)}
          >
            ↩ 引用
          </button>
          {meta?.fromMailbox ? (
            <span className="mr-1 rounded bg-accent/20 px-1 text-accent">离线消息</span>
          ) : null}
          <span>{new Date(msg.ts).toLocaleTimeString()} {out ? statusText[msg.status] : ''}</span>
        </div>
      </div>
    </div>
  );
}

/**
 * 聊天页：会话列表（左）+ 消息面板（右）。
 * 依赖会话装配（解锁后 `startSession`）；未装配时给出提示而非崩溃。
 */
export function ChatListPage(): JSX.Element {
  const contacts = useChatStore((s) => s.contacts);
  const messagesByPeer = useChatStore((s) => s.messagesByPeer);
  const activePeerId = useChatStore((s) => s.activePeerId);
  const myPeerId = useIdentityStore((s) => s.peerId);
  const did = useIdentityStore((s) => s.did);
  const name = useIdentityStore((s) => s.name);
  const status = useIdentityStore((s) => s.status);
  const sessionReady = container.hasPeerClient();
  /** 在线目录（心跳每 ~30s 刷新）：peerId 集合 + 最近在线时间 */
  const onlinePeers = useNetworkStore((s) => s.online);

  const [draft, setDraft] = useState('');
  const [addId, setAddId] = useState('');
  const [busy, setBusy] = useState(false);
  const [retrying, setRetrying] = useState(false);
  /** 扫一扫 / 我的二维码 弹窗 */
  const [scanOpen, setScanOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [scanBusy, setScanBusy] = useState(false);
  const [payBusy, setPayBusy] = useState(false);
  /** 我的二维码弹窗 tab：add=好友码 / pay=收款码 */
  const [qrTab, setQrTab] = useState<'add' | 'pay'>('add');
  const [qrPayAmount, setQrPayAmount] = useState('');
  /** 会话安全码弹窗（W-CH-13） */
  const [safetyOpen, setSafetyOpen] = useState(false);
  const [safetyCode, setSafetyCode] = useState('');
  /** 长按 / 右键唤起的联系人操作菜单目标 */
  const [menuPeerId, setMenuPeerId] = useState<string | null>(null);
  /** 联系人备注（v3.10）：本地个性化显示名 */
  const [notes, setNotes] = useState<Record<string, string>>(() => loadContactNotes());
  const [notePeerId, setNotePeerId] = useState<string | null>(null);
  const [noteDraft, setNoteDraft] = useState('');
  const longPressTimer = useRef<number | null>(null);
  const { copied, copy } = useCopy();

  // ───────── 媒体消息 / 引用回复（v3.11） ─────────
  const [quoteMsg, setQuoteMsg] = useState<Message | null>(null);
  const [mediaBusy, setMediaBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recChunksRef = useRef<Blob[]>([]);
  const recStartRef = useRef(0);

  // ───────── v3.22 红包（单聊：链上直转 + 红包消息卡片） ─────────
  const [redpackOpen, setRedpackOpen] = useState(false);
  const [rpAmount, setRpAmount] = useState('1');
  const [rpNote, setRpNote] = useState('恭喜发财');
  const [rpSending, setRpSending] = useState(false);

  // ───────── v3.23 通知中心 ─────────
  const [noticeOpen, setNoticeOpen] = useState(false);
  const notices = useNoticeStore((s) => s.notices);
  const unreadNotices = useNoticeStore((s) => s.notices.filter((n) => !n.read).length);

  // ───────── 消息操作（v3.13：长按 保存/分享/收藏） ─────────
  const [actionMsg, setActionMsg] = useState<Message | null>(null);
  const [actionPos, setActionPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [shareOpen, setShareOpen] = useState(false);
  const [shareBusy, setShareBusy] = useState(false);
  const [saveBusy, setSaveBusy] = useState(false);

  const onMessageAction = (m: Message, x: number, y: number): void => {
    setActionMsg(m);
    setActionPos({ x, y });
  };

  /** 保存：文本→复制；媒体/文件→拉取 bytes 下载到本地。 */
  const saveMessage = async (m: Message): Promise<void> => {
    if (m.kind === 'text') {
      copy(m.body || '');
      useUiStore.getState().pushToast('success', '文本已复制');
      setActionMsg(null);
      return;
    }
    if (!m.media?.cid) {
      useUiStore.getState().pushToast('info', '该消息没有可保存的内容');
      setActionMsg(null);
      return;
    }
    setSaveBusy(true);
    try {
      const res = await container.feed.getImage(m.media.cid);
      if (!res) throw new Error('no bytes');
      const blob = new Blob([new Uint8Array(res.bytes)], {
        type: m.media.mime || res.mime || 'application/octet-stream',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = m.media.name || (m.kind === 'image' ? '图片.jpg' : m.kind);
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
      useUiStore.getState().pushToast('success', '已保存到本地');
    } catch {
      useUiStore.getState().pushToast('error', '保存失败，请重试');
    } finally {
      setSaveBusy(false);
      setActionMsg(null);
    }
  };

  /** 收藏：写入本机 localStorage，可在「我的」页查看并跳回会话。 */
  const favoriteMessage = (m: Message): void => {
    const convId = activeGroupId ?? activePeerId;
    if (!convId) {
      useUiStore.getState().pushToast('info', '请先打开一个会话');
      return;
    }
    saveFavorite({
      key: `${convId}:${m.msgId}`,
      ts: m.ts,
      convId,
      convKind: activeGroupId ? 'group' : 'peer',
      kind: m.kind,
      summary: summarizeMessage(m),
      msgId: m.msgId,
    });
    useUiStore.getState().pushToast('success', '已收藏');
    setActionMsg(null);
  };

  /** 分享/转发：选择好友或群，把原消息（文本/媒体/红包）转发过去。
   *  v3.24 修复：① 系统消息拦截；② 去掉跨会话 replyTo（引用在目标会话无意义）；
   *  ③ 转发失败（如群成员未握手）给出友好提示而非裸报错。 */
  const shareTo = async (target: { kind: 'peer' | 'group'; id: string; label: string }): Promise<void> => {
    const m = actionMsg;
    if (!m) return;
    if (m.kind === 'system') {
      useUiStore.getState().pushToast('info', '系统消息不可转发');
      setActionMsg(null);
      return;
    }
    setShareBusy(true);
    try {
      const text = m.kind === 'text' ? (m.body || '') : '';
      if (target.kind === 'peer') {
        await container.chat.sendText(target.id, text, { media: m.media });
      } else {
        await container.chat.sendGroupMessage(target.id, text, { media: m.media });
      }
      useUiStore.getState().pushToast('success', `已转发到「${target.label}」`);
      setShareOpen(false);
      setActionMsg(null);
    } catch (err) {
      const code = err instanceof MeshLinkError ? err.code : 'E_UNKNOWN';
      if (code === 'E_INVALID_INPUT') {
        useUiStore.getState().pushToast('error', '该目标暂时无法接收消息（成员未建立连接）');
      } else {
        useUiStore.getState().pushError(code);
      }
    } finally {
      setShareBusy(false);
    }
  };

  // ───────── 群聊（v3.9） ─────────
  const groups = useGroupStore((s) => s.groups);
  const activeGroupId = useGroupStore((s) => s.activeGroupId);
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [groupBusy, setGroupBusy] = useState(false);

  const activeGroup = useMemo(
    () => groups.find((g) => g.id === activeGroupId) ?? null,
    [groups, activeGroupId],
  );
  const groupMessages = activeGroupId ? messagesByPeer[activeGroupId] ?? [] : [];
  const groupMembers = activeGroup?.members ?? [];

  /** 分享/转发目标（v3.13）：好友 + 可接收的群聊。
   *  v3.24 修复：群需至少 1 个「非自己、已握手、未屏蔽」成员才可转发，
   *  否则 sendGroupMessage 会全部 skipped 抛 E_INVALID_INPUT（复现：邀请链接入群但未互加好友的群）。 */
  const shareTargets = useMemo(() => {
    const peers = contacts.map((c) => ({
      kind: 'peer' as const,
      id: c.peerId,
      label: `${resolveContactName(notes[c.peerId], c.name || shortId(c.peerId))} · ${shortId(c.peerId)}`,
    }));
    const peerMap = new Map(contacts.map((c) => [c.peerId, c]));
    const gs = groups
      .filter((g) => (g.members ?? []).some((mid) => mid !== myPeerId && peerMap.has(mid)))
      .map((g) => ({ kind: 'group' as const, id: g.id, label: `${g.name}（群）` }));
    return [...peers, ...gs];
  }, [contacts, groups, notes, myPeerId]);

  useEffect(() => {
    if (!sessionReady) return;
    try {
      container.groups.start();
    } catch {
      /* 传输层未装配时群列表保持本地 */
    }
  }, [sessionReady]);

  /** v3.18 群裂变：消费邀请链接（LS 由 App 挂载时写入）→ 自动加入群；传输未就绪则 1.5s 轮询重试。 */
  useEffect(() => {
    if (status !== 'unlocked') return;
    let active = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const tryJoin = (): void => {
      const invite = localStorage.getItem('mesh_group_invite');
      if (!invite) return;
      if (!container.hasPeerClient()) {
        if (active) timer = setTimeout(tryJoin, 1500);
        return;
      }
      localStorage.removeItem('mesh_group_invite');
      void container.groups
        .joinViaInvite(invite)
        .then((g) => {
          if (!active) return;
          useUiStore.getState().pushToast('success', `已通过邀请加入「${g.name}」`);
        })
        .catch((err) => {
          if (!active) return;
          useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
        });
    };
    tryJoin();
    return () => {
      active = false;
      if (timer) clearTimeout(timer);
    };
  }, [status]);

  /** v3.18 群裂变：复制群邀请链接（好友打开即加入）。 */
  const copyGroupInvite = async (gid: string, gname: string): Promise<void> => {
    const url = `${window.location.origin}/chat?invite=${gid}`;
    await navigator.clipboard?.writeText(url).catch(() => undefined);
    useUiStore.getState().pushToast('success', `群「${gname}」邀请链接已复制`);
  };

  /** 打开群会话（与单聊互斥）：先同步切面板，再异步加载历史（不阻塞面板渲染）。 */
  const openGroup = (groupId: string): void => {
    useChatStore.getState().setActive(null);
    useGroupStore.getState().setActive(groupId);
    useGroupStore.getState().markRead(groupId);
    void container.chat.loadMessages(groupId).catch(() => undefined);
  };

  /** 新建群：先关弹窗（防止 invite 慢/失败挡面板），再建群、邀请、进群。 */
  const createGroup = async (): Promise<void> => {
    const gname = groupName.trim();
    if (!gname) {
      useUiStore.getState().pushError('E_INVALID_INPUT');
      return;
    }
    setGroupModalOpen(false);
    setGroupName('');
    const peers = [...picked];
    setPicked(new Set());
    setGroupBusy(true);
    try {
      const g = await container.groups.create(gname);
      if (peers.length > 0) {
        await container.groups.invite(g.id, peers).catch(() => undefined);
      }
      openGroup(g.id);
      useUiStore.getState().pushToast('success', `已创建群「${g.name}」`);
    } catch (err) {
      if (err instanceof MeshLinkError && err.code === 'E_NOT_UNLOCKED') {
        useUiStore.getState().pushToast('info', '请先解锁身份再建群');
      } else {
        useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
      }
    } finally {
      setGroupBusy(false);
    }
  };

  /** 邀请好友入群（加人弹窗）：先关弹窗，邀请失败单独提示不挡面板。 */
  const inviteGroup = async (): Promise<void> => {
    if (!activeGroup || picked.size === 0) return;
    const peers = [...picked];
    setPicked(new Set());
    setInviteModalOpen(false);
    setGroupBusy(true);
    try {
      await container.groups.invite(activeGroup.id, peers);
      useUiStore.getState().pushToast('success', '已邀请成员');
    } catch (err) {
      useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
    } finally {
      setGroupBusy(false);
    }
  };

  /** 退出群。 */
  const leaveGroup = async (): Promise<void> => {
    if (!activeGroup) return;
    setGroupBusy(true);
    try {
      await container.groups.leave(activeGroup.id);
      useChatStore.getState().setActive(null);
      useUiStore.getState().pushToast('success', '已退出群聊');
    } catch (err) {
      useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
    } finally {
      setGroupBusy(false);
    }
  };

  /** 群消息发送（逐成员加密信封，由 ChatService 处理）。 */
  const sendGroup = async (): Promise<void> => {
    const text = draft.trim();
    if (!text || !activeGroupId) return;
    setDraft('');
    const replyTo = quoteMsg?.msgId;
    setQuoteMsg(null);
    try {
      await container.chat.sendGroupMessage(activeGroupId, text, { replyTo });
    } catch (err) {
      useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
    }
  };

  /** 单聊发送：文本（可带引用）。 */
  const send = async (): Promise<void> => {
    const text = draft.trim();
    if (!text || !activePeerId) return;
    setDraft('');
    const replyTo = quoteMsg?.msgId;
    setQuoteMsg(null);
    try {
      await container.chat.sendText(activePeerId, text, { replyTo });
    } catch (err) {
      useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
    }
  };

  /** v3.22 单聊红包：链上直转对端 + 发送红包消息（text 承载 JSON，协议兼容）。 */
  const sendRedpack = async (): Promise<void> => {
    if (!activePeerId || rpSending) return;
    const n = Number(rpAmount);
    if (!Number.isFinite(n) || n <= 0) {
      useUiStore.getState().pushToast('error', '金额不正确');
      return;
    }
    const contact = contacts.find((c) => c.peerId === activePeerId);
    const peerDid = contact?.did;
    if (!peerDid) {
      useUiStore.getState().pushToast('error', '对方尚未完成身份交换');
      return;
    }
    setRpSending(true);
    try {
      const toAddr = peerDid.startsWith('did:dpb:') ? peerDid.slice('did:dpb:'.length) : peerDid;
      const tx = await container.wallet.transfer(toAddr, rpAmount);
      await container.chat.sendText(
        activePeerId,
        JSON.stringify({ redpack: true, amountDpb: rpAmount, note: rpNote || '恭喜发财', txHash: tx.txHash }),
      );
      useUiStore.getState().pushToast('success', `红包已发出（${rpAmount} DPB 已上链转账）`);
      setRedpackOpen(false);
      setRpNote('恭喜发财');
    } catch (err) {
      useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
    } finally {
      setRpSending(false);
    }
  };

  /** 图片/文件消息发送（v3.11）：上传 → cid → 媒体消息（单聊/群聊通用）。 */
  const sendMedia = async (files: FileList | File[]): Promise<void> => {
    const groupMode = Boolean(activeGroupId);
    const dmMode = Boolean(activePeerId);
    if (!groupMode && !dmMode) return;
    setMediaBusy(true);
    try {
      for (const file of Array.from(files)) {
        if (!file || !file.size) continue;
        // 按 MIME + 扩展名双保险识别媒体类型（上传通道可能不携带 MIME）
        const lower = file.name.toLowerCase();
        const kind = file.type.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|avif|heic)$/.test(lower)
          ? ('image' as const)
          : file.type.startsWith('video/') || /\.(mp4|webm|mov|m4v|avi|mkv|3gp)$/.test(lower)
            ? ('video' as const)
            : file.type.startsWith('audio/') || /\.(mp3|wav|ogg|oga|m4a|aac|flac|opus)$/.test(lower)
              ? ('audio' as const)
              : ('file' as const);
        const media = await container.feed.uploadChatMedia(file, kind);
        const replyTo = quoteMsg?.msgId;
        setQuoteMsg(null);
        if (groupMode) await container.chat.sendGroupMessage(activeGroupId!, '', { media, replyTo });
        else await container.chat.sendText(activePeerId!, '', { media, replyTo });
        const label =
          kind === 'image' ? '图片' : kind === 'video' ? '视频' : kind === 'audio' ? '音频' : '文件';
        useUiStore.getState().pushToast('success', `${label}已发送`);
      }
    } catch (err) {
      useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
    } finally {
      setMediaBusy(false);
    }
  };

  /** 语音消息（v3.11）：MediaRecorder 录音 → 上传 → 媒体消息。 */
  const startRecord = async (): Promise<void> => {
    if (recording) return;
    if (!activeGroupId && !activePeerId) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      recChunksRef.current = [];
      mr.ondataavailable = (e: BlobEvent) => {
        if (e.data && e.data.size > 0) recChunksRef.current.push(e.data);
      };
      mr.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        void (async () => {
          const duration = Date.now() - recStartRef.current;
          const blob = new Blob(recChunksRef.current, { type: mr.mimeType || 'audio/webm' });
          if (blob.size === 0) {
            useUiStore.getState().pushToast('info', '录音太短，未发送');
            return;
          }
          const file = new File([blob], `voice-${Date.now()}.webm`, { type: blob.type || 'audio/webm' });
          try {
            const media = await container.feed.uploadChatMedia(file, 'audio');
            media.duration = duration;
            const replyTo = quoteMsg?.msgId;
            setQuoteMsg(null);
            if (activeGroupId) await container.chat.sendGroupMessage(activeGroupId, '', { media, replyTo });
            else if (activePeerId) await container.chat.sendText(activePeerId, '', { media, replyTo });
            useUiStore.getState().pushToast('success', '语音已发送');
          } catch (err) {
            useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
          }
        })();
      };
      mr.start();
      mediaRecorderRef.current = mr;
      recStartRef.current = Date.now();
      setRecording(true);
    } catch {
      useUiStore.getState().pushToast('info', '无法访问麦克风');
    }
  };

  const stopRecord = (): void => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setRecording(false);
  };

  const cancelLongPress = (): void => {
    if (longPressTimer.current !== null) {
      window.clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  /** 长按 500ms 唤起菜单（移动端），桌面端同时支持右键。 */
  const startLongPress = (peerId: string): void => {
    cancelLongPress();
    longPressTimer.current = window.setTimeout(() => {
      setMenuPeerId(peerId);
      longPressTimer.current = null;
    }, 500);
  };

  useEffect(() => cancelLongPress, []);

  useEffect(() => {
    if (!sessionReady) return;
    void container.chat.refreshContacts().catch(() => undefined);
  }, [sessionReady]);

  const active = useMemo(
    () => contacts.find((c) => c.peerId === activePeerId) ?? null,
    [contacts, activePeerId],
  );
  const messages = activePeerId ? messagesByPeer[activePeerId] ?? [] : [];

  /** 在线判定集合（在线目录 peerId → connectedAt）。 */
  const onlineAt = useMemo(() => {
    const m = new Map<string, number>();
    for (const p of onlinePeers) m.set(p.peerId, p.connectedAt);
    return m;
  }, [onlinePeers]);

  /** 在线联系人在前，其余按最近消息时间排序。 */
  const sortedContacts = useMemo(() => {
    const list = [...contacts];
    list.sort((a, b) => {
      const ao = onlineAt.has(a.peerId) ? 0 : 1;
      const bo = onlineAt.has(b.peerId) ? 0 : 1;
      if (ao !== bo) return ao - bo;
      return (b.lastTs ?? b.updatedAt) - (a.lastTs ?? a.updatedAt);
    });
    return list;
  }, [contacts, onlineAt]);

  const onlineCount = sortedContacts.filter((c) => onlineAt.has(c.peerId)).length;
  const activeOnline = activePeerId ? onlineAt.has(activePeerId) : false;
  /** 群成员在线计数（v3.9.4：onlineAt 声明后计算，避免 TDZ 崩溃） */
  const groupOnlineCount = groupMembers.filter((m) => onlineAt.has(m)).length;

  const openChat = async (peerId: string): Promise<void> => {
    useGroupStore.getState().setActive(null);
    useChatStore.getState().setActive(peerId);
    try {
      await container.chat.loadMessages(peerId);
      await container.chat.markRead(peerId);
    } catch {
      /* 未装配 / 离线时静默，列表仍可浏览 */
    }
  };

  const add = async (): Promise<void> => {
    const id = addId.trim();
    if (!id) {
      useUiStore.getState().pushError('E_INVALID_INPUT');
      return;
    }
    setBusy(true);
    try {
      await container.chat.addContact(id, '');
      setAddId('');
      useUiStore.getState().pushToast('success', '已发送好友请求');
    } catch (err) {
      useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
    } finally {
      setBusy(false);
    }
  };

  /** 扫码结果确认添加（复用 addContact 链路，二维码自动携带 peerId + 昵称）。返回是否成功。 */
  const scanAdd = async (c: QrContact): Promise<boolean> => {
    setScanBusy(true);
    try {
      await container.chat.addContact(c.peerId, c.name ?? '');
      useUiStore.getState().pushToast('success', `已发送好友请求：${c.name || c.peerId.slice(0, 8)}…`);
      setScanOpen(false);
      void container.chat.refreshContacts().catch(() => undefined);
      return true;
    } catch (err) {
      useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
      return false;
    } finally {
      setScanBusy(false);
    }
  };

  /** 扫码收款码 → 付款：复用身份钱包 transfer 签名上链。 */
  const scanPay = async (toAddr: string, amountDpb: string): Promise<boolean> => {
    if (!toAddr || !amountDpb) {
      useUiStore.getState().pushError('E_INVALID_INPUT');
      return false;
    }
    setPayBusy(true);
    try {
      await container.wallet.transfer(toAddr, amountDpb);
      useUiStore.getState().pushToast('success', `已向 ${shortId(toAddr, 8, 6)} 支付 ${amountDpb} DPB`);
      setScanOpen(false);
      return true;
    } catch (err) {
      const code = err instanceof MeshLinkError ? err.code : 'E_UNKNOWN';
      useUiStore.getState().pushError(
        code,
        code === 'E_NONCE_CONFLICT' ? { onAction: () => void scanPay(toAddr, amountDpb) } : undefined,
      );
      return false;
    } finally {
      setPayBusy(false);
    }
  };

  const toggleBlock = async (peerId: string, blocked: boolean): Promise<void> => {
    try {
      await container.chat.setBlocked(peerId, blocked);
      useUiStore.getState().pushToast('success', blocked ? '已屏蔽' : '已解除屏蔽');
    } catch (err) {
      useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
    }
  };

  /**
   * 手动重发尚未握手的 intro。
   *
   * `ChatService` 的日志文案写着「可在 UI 手动重试」，但此前全仓无任何重试入口 ——
   * 本函数补上该缺口：pending 且未拉黑的联系人可由用户主动重发握手邀请。
   */
  const resendIntro = async (): Promise<void> => {
    setRetrying(true);
    try {
      const n = await container.chat.resendPendingIntros();
      if (n > 0) {
        // 重新武装退避环：补发环有 60s 上限会自行收环，只补发一次等于没兑现「重试」预期。
        // startIntroRetry() 幂等（内部已有 timer 则直接返回），重复点击无副作用。
        container.chat.startIntroRetry();
        useUiStore.getState().pushToast('success', '已重新发送握手邀请，并已重启自动补发');
      } else {
        useUiStore.getState().pushToast('info', '当前没有待握手的联系人');
      }
    } catch (err) {
      useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
    } finally {
      setRetrying(false);
    }
  };

  const removeContact = async (peerId: string): Promise<void> => {
    try {
      await container.chat.removeContact(peerId);
      useUiStore.getState().pushToast('success', '已移除好友');
    } catch (err) {
      useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
    }
  };

  /** 打开会话安全码：双方从各自公钥派生同一 6 位短码，一致即无中间人（W-CH-13）。 */
  const openSafety = async (): Promise<void> => {
    if (!activePeerId) return;
    try {
      const code = await container.chat.safetyCode(activePeerId);
      setSafetyCode(code);
      setSafetyOpen(true);
    } catch (err) {
      useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
    }
  };

  return (
    <div className="grid gap-4 md:grid-cols-[300px_1fr]">
      {/* ───────── 会话列表 ───────── */}
      <div className="card flex min-h-[300px] flex-col p-4">
        <div className="mb-3 flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setQrOpen(true)}>
            我的二维码
          </Button>
          <Button size="sm" variant="outline" onClick={() => setScanOpen(true)}>
            扫一扫
          </Button>
          {/* v3.23 通知中心入口 */}
          <div className="relative ml-auto">
            <Button size="sm" variant="outline" onClick={() => setNoticeOpen((v) => !v)}>
              🔔{unreadNotices > 0 ? <span className="ml-1 text-accent">· {unreadNotices}</span> : null}
            </Button>
            {noticeOpen ? (
              <div className="absolute right-0 top-full z-50 mt-1 w-80 max-w-[calc(100vw-2rem)] rounded-card border border-line bg-panel shadow-xl">
                <div className="flex items-center justify-between border-b border-line px-3 py-2">
                  <p className="text-xs font-medium text-text">通知中心{unreadNotices ? `（${unreadNotices} 条未读）` : ''}</p>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      className="text-[11px] text-dim hover:text-text"
                      onClick={() => useNoticeStore.getState().markAllRead()}
                    >
                      全部已读
                    </button>
                    <button
                      type="button"
                      className="text-[11px] text-dim hover:text-err"
                      onClick={() => useNoticeStore.getState().clearNotices()}
                    >
                      清空
                    </button>
                  </div>
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {notices.length === 0 ? (
                    <p className="px-3 py-6 text-center text-xs text-dim">暂无通知</p>
                  ) : (
                    notices.map((nt) => (
                      <button
                        key={nt.id}
                        type="button"
                        onClick={() => {
                          useNoticeStore.getState().markAllRead();
                          setNoticeOpen(false);
                        }}
                        className={`block w-full border-b border-line/60 px-3 py-2 text-left hover:bg-panel-2 ${
                          nt.read ? 'opacity-60' : ''
                        }`}
                      >
                        <p className="text-xs text-text">{nt.title}</p>
                        <p className="mt-0.5 line-clamp-2 text-[11px] text-dim">{nt.body}</p>
                        <p className="mt-0.5 text-[10px] text-dim/60">
                          {new Date(nt.ts).toLocaleString('zh-CN', { hour12: false })}
                        </p>
                      </button>
                    ))
                  )}
                </div>
              </div>
            ) : null}
          </div>
        </div>
        <div className="mb-3 flex gap-2">
          <Input
            value={addId}
            onChange={(e) => setAddId(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void add();
            }}
            placeholder="对方 PeerID"
          />
          <Button size="sm" loading={busy} onClick={() => void add()}>
            添加
          </Button>
        </div>

        {/* 群聊区（v3.9） */}
        <div className="mb-1 flex items-center justify-between">
          <p className="text-[11px] font-medium text-dim">群聊 · {groups.length}</p>
          <Button size="sm" variant="ghost" onClick={() => setGroupModalOpen(true)}>
            ＋ 新建群
          </Button>
        </div>
        {groups.length > 0 ? (
          <ul className="mb-3 -mx-1 space-y-1">
            {groups.map((g) => (
              <li key={g.id}>
                <button
                  type="button"
                  onClick={() => void openGroup(g.id)}
                  className={`flex w-full items-center justify-between rounded-btn px-2 py-2 text-left transition-colors ${
                    g.id === activeGroupId ? 'bg-panel-2' : 'hover:bg-panel-2'
                  }`}
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm text-text">
                      <span className="mr-1.5 inline-block h-2 w-2 rounded-full bg-accent align-middle" />
                      {g.name || g.id.slice(0, 8)}
                    </p>
                    <p className="truncate text-[11px] text-dim">
                      {(g.members ?? []).length} 位成员 · {g.lastText || '还没有消息'}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {g.unread > 0 ? (
                      <Badge tone="gold">{g.unread > 99 ? '99+' : g.unread}</Badge>
                    ) : null}
                    <button
                      type="button"
                      aria-label="复制群邀请链接"
                      title="复制群邀请链接（v3.18 群裂变）"
                      className="text-[11px] text-dim hover:text-accent"
                      onClick={(e) => {
                        e.stopPropagation();
                        void copyGroupInvite(g.id, g.name || g.id.slice(0, 8));
                      }}
                    >
                      🔗
                    </button>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mb-3 text-[11px] text-dim">还没有群，点「＋ 新建群」邀请好友一起聊</p>
        )}

        {contacts.length === 0 ? (
          <EmptyState title="还没有会话" description="添加好友后即可开始加密聊天" />
        ) : (
          <>
            <p className="mb-2 text-[11px] text-dim">
              {onlineCount > 0 ? (
                <>
                  <span className="text-ok">● {onlineCount} 在线</span>
                  <span className="mx-1">·</span>
                  <span className="text-dim">● {contacts.length - onlineCount} 离线</span>
                </>
              ) : (
                <span className="text-dim">● {contacts.length} 个联系人（暂无在线）</span>
              )}
            </p>
            <ul className="-mx-1 space-y-1 overflow-y-auto">
            {sortedContacts.map((c) => (
              <li key={c.peerId}>
                <div className="relative">
                  {menuPeerId === c.peerId ? (
                    <div className="absolute right-1 top-full z-20 w-32 overflow-hidden rounded-btn border border-line bg-panel shadow-lg">
                      <button
                        type="button"
                        className="block w-full px-3 py-2 text-left text-xs text-text hover:bg-panel-2"
                        onClick={() => {
                          setMenuPeerId(null);
                          setNotePeerId(c.peerId);
                          setNoteDraft(notes[c.peerId] ?? '');
                        }}
                      >
                        {notes[c.peerId] ? '修改备注' : '设置备注'}
                      </button>
                      <button
                        type="button"
                        className="block w-full px-3 py-2 text-left text-xs text-text hover:bg-panel-2"
                        onClick={() => {
                          setMenuPeerId(null);
                          void toggleBlock(c.peerId, !c.blocked);
                        }}
                      >
                        {c.blocked ? '解除屏蔽' : '屏蔽'}
                      </button>
                      <button
                        type="button"
                        className="block w-full px-3 py-2 text-left text-xs text-err hover:bg-panel-2"
                        onClick={() => {
                          setMenuPeerId(null);
                          void removeContact(c.peerId);
                        }}
                      >
                        移除好友
                      </button>
                    </div>
                  ) : null}

                  <button
                  type="button"
                  onClick={() => {
                    setMenuPeerId(null);
                    void openChat(c.peerId);
                  }}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    setMenuPeerId(c.peerId);
                  }}
                  onTouchStart={() => startLongPress(c.peerId)}
                  onTouchEnd={cancelLongPress}
                  onTouchMove={cancelLongPress}
                  className={`flex w-full items-center justify-between rounded-btn px-2 py-2 text-left transition-colors ${
                    c.peerId === activePeerId ? 'bg-panel-2' : 'hover:bg-panel-2'
                  }`}
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm text-text">
                      <span
                        className={`mr-1.5 inline-block h-2 w-2 rounded-full align-middle ${
                          onlineAt.has(c.peerId) ? 'bg-ok' : 'bg-dim'
                        }`}
                        aria-label={onlineAt.has(c.peerId) ? '在线' : '离线'}
                      />
                      {resolveContactName(notes[c.peerId], c.name || shortId(c.peerId, 8, 4))}
                      {c.blocked ? <span className="ml-1 text-[10px] text-err">已屏蔽</span> : null}
                    </p>
                    <p className="truncate text-[11px] text-dim">
                      <span className={onlineAt.has(c.peerId) ? 'text-ok' : ''}>
                        {onlineAt.has(c.peerId) ? '在线' : '离线'}
                      </span>
                      <span className="mx-1">·</span>
                      {c.lastText || (c.introState === 'ready' ? '已建立会话' : '等待身份交换…')}
                    </p>
                  </div>
                  {c.unread > 0 ? (
                    <Badge tone="gold">{c.unread > 99 ? '99+' : c.unread}</Badge>
                  ) : null}
                  </button>
                </div>
              </li>
            ))}
            </ul>
          </>
        )}
      </div>

      {/* ───────── 消息面板 ───────── */}
      <div className="card flex min-h-[420px] flex-col p-4">
        {!sessionReady ? (
          <div className="flex flex-1 items-center justify-center">
            <EmptyState title="网络会话未就绪" description="解锁后会自动装配传输层；请稍候或检查中继连接" />
          </div>
        ) : activeGroup ? (
          /* ───────── 群消息面板（v3.9） ───────── */
          <GroupPanelBoundary>
          <>
            <header className="mb-3 flex items-center justify-between border-b border-line pb-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-text">
                  <span className="mr-1.5 inline-block h-2 w-2 rounded-full bg-accent align-middle" />
                  {activeGroup.name || activeGroup.id.slice(0, 8)}
                  <span className="ml-1 text-[11px] text-dim">
                    {groupMembers.length} 位成员 · {groupOnlineCount} 在线
                  </span>
                </p>
                <p className="mono truncate text-[11px] text-dim">
                  群 ID {activeGroup.id.slice(0, 12)}… · 群主 {shortId(activeGroup.creator, 6, 4)}
                </p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setInviteModalOpen(true)}>
                  加人
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  loading={groupBusy}
                  onClick={() => void leaveGroup()}
                >
                  退出
                </Button>
              </div>
            </header>

            <div className="flex-1 space-y-2 overflow-y-auto px-1">
              {groupMessages.length === 0 ? (
                <div className="mt-6 text-center">
                  <p className="text-xs text-dim">群聊已建好，说点什么吧</p>
                </div>
              ) : (
                groupMessages.map((m) => {
                  const meta = m.meta as { groupId?: string; fromPeer?: string } | undefined;
                  const senderName =
                    m.dir === 'in' && meta?.fromPeer ? groupMemberName(meta.fromPeer) : undefined;
                  return (
                    <Bubble
                      key={m.msgId}
                      msg={m}
                      senderName={senderName}
                      allMsgs={groupMessages}
                      onQuote={setQuoteMsg}
                      onAction={onMessageAction}
                    />
                  );
                })
              )}
            </div>

            <div className="mt-3 border-t border-line pt-3">
              {quoteMsg ? (
                <div className="mb-1 flex items-center justify-between gap-2 rounded border border-line bg-panel px-2 py-1 text-[11px] text-dim">
                  <span className="truncate">↩ 引用：{summarizeMessage(quoteMsg)}</span>
                  <button type="button" className="ml-1 shrink-0 text-err" onClick={() => setQuoteMsg(null)}>
                    ×
                  </button>
                </div>
              ) : null}
              <div className="flex gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files?.length) void sendMedia(e.target.files);
                    e.target.value = '';
                  }}
                />
                <Button
                  size="sm"
                  variant="ghost"
                  title="图片 / 文件"
                  loading={mediaBusy}
                  disabled={!sessionReady}
                  onClick={() => fileInputRef.current?.click()}
                >
                  ＋
                </Button>
                <Button
                  size="sm"
                  variant={recording ? 'primary' : 'ghost'}
                  title={recording ? '停止录音' : '语音消息'}
                  disabled={!sessionReady}
                  onClick={() => {
                    if (recording) stopRecord();
                    else void startRecord();
                  }}
                >
                  {recording ? '● 录音中' : '🎤'}
                </Button>
                {activePeerId && !activeGroupId ? (
                  <Button size="sm" variant="ghost" title="发红包（DPB 链上转账）" disabled={!sessionReady} onClick={() => setRedpackOpen((v) => !v)}>
                    🧧
                  </Button>
                ) : null}
                <Input
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) void sendGroup();
                  }}
                  placeholder="输入群消息，回车发送"
                />
                <Button size="sm" disabled={draft.trim().length === 0} onClick={() => void sendGroup()}>
                  发送
                </Button>
              </div>
              {redpackOpen && activePeerId && !activeGroupId ? (
                <div className="mt-2 space-y-2 rounded-btn bg-panel-2 p-3">
                  <p className="text-xs font-medium text-text">🧧 发红包（链上直转 DPB）</p>
                  <div className="flex flex-wrap gap-1.5">
                    {['0.1', '0.5', '1', '5', '10'].map((v) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => setRpAmount(v)}
                        className={`rounded-full px-2.5 py-1 text-[11px] ${
                          rpAmount === v ? 'bg-accent/20 text-accent' : 'bg-panel-1 text-dim hover:text-text'
                        }`}
                      >
                        {v} DPB
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] text-dim">金额</span>
                    <Input value={rpAmount} onChange={(e) => setRpAmount(e.target.value)} inputMode="decimal" placeholder="自定义" />
                    <span className="text-[11px] text-dim">祝福语</span>
                    <Input value={rpNote} onChange={(e) => setRpNote(e.target.value)} placeholder="恭喜发财" />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setRedpackOpen(false)}>
                      取消
                    </Button>
                    <Button size="sm" loading={rpSending} onClick={() => void sendRedpack()}>
                      塞钱进红包
                    </Button>
                  </div>
                </div>
              ) : null}
            </div>
          </>
          </GroupPanelBoundary>
        ) : !active ? (
          <div className="flex flex-1 items-center justify-center">
            <EmptyState title="选择一条会话" description="从左侧选择联系人开始聊天" />
          </div>
        ) : (
          <>
            <header className="mb-3 flex items-center justify-between border-b border-line pb-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-text">
                  <span
                    className={`mr-1.5 inline-block h-2 w-2 rounded-full align-middle ${
                      activeOnline ? 'bg-ok' : 'bg-dim'
                    }`}
                  />
                  {resolveContactName(notes[active.peerId], active.name || shortId(active.peerId, 8, 4))}
                  <span className={`ml-1 text-[11px] ${activeOnline ? 'text-ok' : 'text-dim'}`}>
                    {activeOnline ? '在线' : '离线'}
                  </span>
                </p>
                <p className="mono truncate text-[11px] text-dim">
                  我的 {shortId(myPeerId, 6, 4)} ↔ {shortId(active.peerId, 6, 4)}
                </p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => void openSafety()}>
                  安全码
                </Button>
                <Button
                  variant={active.blocked ? 'ghost' : 'danger'}
                  size="sm"
                  onClick={() => void toggleBlock(active.peerId, !active.blocked)}
                >
                  {active.blocked ? '解除拉黑' : '拉黑'}
                </Button>
              </div>
            </header>

            <div className="flex-1 space-y-2 overflow-y-auto px-1">
              {messages.length === 0 ? (
                <div className="mt-6 space-y-3 text-center">
                  <p className="text-xs text-dim">
                    {active.introState === 'ready'
                      ? '开始你们的第一次对话'
                      : '正在交换身份密钥（intro）…若长时间未完成，可重新发送握手邀请。'}
                  </p>
                  {active.introState !== 'ready' && !active.blocked ? (
                    <Button size="sm" variant="ghost" loading={retrying} onClick={() => void resendIntro()}>
                      重新发送邀请
                    </Button>
                  ) : null}
                </div>
              ) : (
                messages.map((m) => (
                  <Bubble
                    key={m.msgId}
                    msg={m}
                    allMsgs={messages}
                    onQuote={setQuoteMsg}
                    onAction={onMessageAction}
                  />
                ))
              )}
            </div>

            <div className="mt-3 border-t border-line pt-3">
              {quoteMsg ? (
                <div className="mb-1 flex items-center justify-between gap-2 rounded border border-line bg-panel px-2 py-1 text-[11px] text-dim">
                  <span className="truncate">↩ 引用：{summarizeMessage(quoteMsg)}</span>
                  <button type="button" className="ml-1 shrink-0 text-err" onClick={() => setQuoteMsg(null)}>
                    ×
                  </button>
                </div>
              ) : null}
              <div className="flex gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files?.length) void sendMedia(e.target.files);
                    e.target.value = '';
                  }}
                />
                <Button
                  size="sm"
                  variant="ghost"
                  title="图片 / 文件"
                  loading={mediaBusy}
                  disabled={active.introState !== 'ready'}
                  onClick={() => fileInputRef.current?.click()}
                >
                  ＋
                </Button>
                <Button
                  size="sm"
                  variant={recording ? 'primary' : 'ghost'}
                  title={recording ? '停止录音' : '语音消息'}
                  disabled={active.introState !== 'ready'}
                  onClick={() => {
                    if (recording) stopRecord();
                    else void startRecord();
                  }}
                >
                  {recording ? '● 录音中' : '🎤'}
                </Button>
                <Input
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) void send();
                  }}
                  placeholder={active.introState === 'ready' ? '输入消息，回车发送' : '等待身份交换完成后可发送'}
                  disabled={active.introState !== 'ready'}
                />
                <Button
                  size="sm"
                  disabled={active.introState !== 'ready' || draft.trim().length === 0}
                  onClick={() => void send()}
                >
                  发送
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* 消息操作菜单（v3.13）：长按/右键消息 → 保存 / 分享 / 收藏 */}
      {actionMsg ? (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setActionMsg(null)}
            onContextMenu={(e) => {
              e.preventDefault();
              setActionMsg(null);
            }}
          />
          <div
            className="fixed z-50 min-w-[168px] rounded-card border border-line bg-panel p-1 shadow-xl"
            style={{
              left: Math.max(8, Math.min(actionPos.x, (typeof window !== 'undefined' ? window.innerWidth : 1200) - 184)),
              top: Math.max(8, Math.min(actionPos.y + 12, (typeof window !== 'undefined' ? window.innerHeight : 800) - 170)),
            }}
          >
            <button
              type="button"
              className="flex w-full items-center gap-2 rounded px-3 py-2 text-left text-xs text-text hover:bg-panel-2"
              onClick={() => void saveMessage(actionMsg)}
            >
              {saveBusy ? <span>保存中…</span> : <>{actionMsg.kind === 'text' ? '📋 复制文本' : '💾 保存到本地'}</>}
            </button>
            <button
              type="button"
              className="flex w-full items-center gap-2 rounded px-3 py-2 text-left text-xs text-text hover:bg-panel-2"
              onClick={() => setShareOpen(true)}
            >
              ↗ 分享 / 转发
            </button>
            <button
              type="button"
              className="flex w-full items-center gap-2 rounded px-3 py-2 text-left text-xs text-text hover:bg-panel-2"
              onClick={() => favoriteMessage(actionMsg)}
            >
              ⭐ 收藏
            </button>
          </div>
        </>
      ) : null}

      {/* 分享/转发目标选择（v3.13）：好友 + 群聊 */}
      <Modal
        open={shareOpen}
        onOpenChange={(open) => {
          if (!open) setShareOpen(false);
        }}
        title="分享 / 转发"
        description="把这条消息转发给好友或群聊（文本 / 图片 / 视频 / 语音 / 红包均可）"
      >
        <div className="max-h-64 space-y-1 overflow-y-auto">
          {shareTargets.length === 0 ? (
            <p className="px-2 py-4 text-center text-xs text-dim">还没有可转发的好友或群，先添加好友吧</p>
          ) : (
            shareTargets.map((t) => (
              <button
                key={`${t.kind}:${t.id}`}
                type="button"
                disabled={shareBusy}
                className="flex w-full items-center gap-2 rounded px-3 py-2 text-left text-xs text-text hover:bg-panel-2 disabled:opacity-50"
                onClick={() => void shareTo(t)}
              >
                <span aria-hidden>{t.kind === 'group' ? '👥' : '👤'}</span>
                <span className="min-w-0 truncate">{t.label}</span>
              </button>
            ))
          )}
        </div>
      </Modal>

      {/* 联系人备注（v3.10）：仅存本机 localStorage，本地显示用 */}
      <Modal
        open={notePeerId !== null}
        onOpenChange={(open) => {
          if (!open) setNotePeerId(null);
        }}
        title="设置备注"
        description="备注只保存在本机，用于本地显示；对方不会看到"
        footer={
          <>
            <Button size="sm" variant="ghost" onClick={() => setNotePeerId(null)}>
              取消
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                if (notePeerId) setNotes(saveContactNote(notePeerId, ''));
                setNotePeerId(null);
              }}
            >
              清除备注
            </Button>
            <Button
              size="sm"
              variant="primary"
              onClick={() => {
                if (notePeerId) setNotes(saveContactNote(notePeerId, noteDraft));
                setNotePeerId(null);
              }}
            >
              保存
            </Button>
          </>
        }
      >
        <Input
          value={noteDraft}
          onChange={(e) => setNoteDraft(e.target.value)}
          placeholder="输入备注名（留空并保存可清除）"
          maxLength={24}
          autoFocus
        />
      </Modal>

      {/* 会话安全码（W-CH-13）：双方比对一致 ⇒ 无中间人 */}
      <Modal
        open={safetyOpen}
        onOpenChange={setSafetyOpen}
        title="会话安全码"
        description="由双方公钥共同派生；与对方屏幕比对，号码一致即确认没有中间人"
        footer={
          <Button size="sm" variant="ghost" onClick={() => void copy(safetyCode, '安全码已复制')}>
            {copied ? '已复制 ✓' : '复制安全码'}
          </Button>
        }
      >
        <p className="mono text-center text-4xl font-bold tracking-[0.3em] text-accent">{safetyCode || '······'}</p>
        <p className="mt-3 text-center text-xs text-dim">
          打开对方设备的同一会话 →「安全码」，比对两组数字。
          若不一致，请立即停止对话并重新建立会话。
        </p>
      </Modal>

      {/* 我的二维码：tab = 好友码 / DPB 收款码 */}
      <Modal
        open={qrOpen}
        onOpenChange={setQrOpen}
        title="我的二维码"
        description="好友码用于加好友；收款码让对方扫码向你付 DPB"
      >
        <div className="mb-3 flex gap-2">
          <Button
            size="sm"
            variant={qrTab === 'add' ? 'primary' : 'ghost'}
            block
            onClick={() => setQrTab('add')}
          >
            加好友
          </Button>
          <Button
            size="sm"
            variant={qrTab === 'pay' ? 'primary' : 'ghost'}
            block
            onClick={() => setQrTab('pay')}
          >
            收款
          </Button>
        </div>
        {qrTab === 'add' ? (
          <IdentityCard did={did} peerId={myPeerId} name={name} />
        ) : (
          <div className="flex flex-col items-center gap-3">
            {container.identity.address ? (
              <>
                <Input
                  value={qrPayAmount}
                  onChange={(e) => setQrPayAmount(e.target.value)}
                  placeholder="收款金额（DPB），留空表示任意金额"
                  inputMode="decimal"
                />
                <QrCodeView
                  value={buildPayUri(container.identity.address, qrPayAmount)}
                  size={208}
                  copyable={false}
                  label="扫一扫向我付款"
                />
                <p className="mono break-all text-[11px] text-dim">{container.identity.address}</p>
                <p className="text-center text-xs text-dim">
                  {qrPayAmount ? `将收取 ${qrPayAmount} DPB` : '任意金额 · 对方输入后确认付款'}
                </p>
              </>
            ) : (
              <p className="text-center text-sm text-dim">未获得链地址，请先解锁钱包</p>
            )}
          </div>
        )}
      </Modal>

      {/* 扫一扫：好友二维码 / DPB 收款码 */}
      <Modal
        open={scanOpen}
        onOpenChange={setScanOpen}
        title="扫一扫"
        description="扫好友码添加好友；扫收款码向对方付款"
      >
        <QrScannerSheet
          myPeerId={myPeerId}
          onAdd={scanAdd}
          busy={scanBusy}
          onPay={scanPay}
          payBusy={payBusy}
        />
      </Modal>

      {/* 新建群（v3.9）：群名 + 勾选好友；未解锁/网络未就绪时先引导解锁，避免「打不开」误报 */}
      <Modal
        open={groupModalOpen}
        onOpenChange={setGroupModalOpen}
        title="新建群聊"
        description="起个群名，勾选要邀请的好友（未勾选可稍后添加）"
        footer={
          <Button
            size="sm"
            loading={groupBusy}
            disabled={!groupName.trim() || !sessionReady}
            onClick={() => void createGroup()}
          >
            {sessionReady ? '创建并进群' : '请先解锁身份'}
          </Button>
        }
      >
        {!sessionReady && (
          <p className="mb-2 rounded-btn bg-warn/10 px-2.5 py-1.5 text-xs text-warn">
            当前身份未解锁或网络未就绪，解锁后才能建群。点击右上角「锁定/解锁」完成解锁。
          </p>
        )}
        <Input
          value={groupName}
          onChange={(e) => setGroupName(e.target.value)}
          placeholder="群名称（最多 24 字）"
          maxLength={24}
        />
        <div className="mt-3 space-y-1">
          {contacts.length === 0 ? (
            <p className="text-xs text-dim">还没有好友，先添加好友再建群</p>
          ) : (
            sortedContacts.map((c) => {
              const on = picked.has(c.peerId);
              return (
                <label
                  key={c.peerId}
                  className="flex cursor-pointer items-center justify-between rounded-btn px-2 py-1.5 hover:bg-panel-2"
                >
                  <span className="min-w-0 truncate text-sm text-text">
                    <span
                      className={`mr-1.5 inline-block h-2 w-2 rounded-full align-middle ${
                        onlineAt.has(c.peerId) ? 'bg-ok' : 'bg-dim'
                      }`}
                    />
                    {c.name || shortId(c.peerId, 8, 4)}
                  </span>
                  <input
                    type="checkbox"
                    className="accent-[var(--accent)]"
                    checked={on}
                    onChange={() => {
                      const next = new Set(picked);
                      if (on) next.delete(c.peerId);
                      else next.add(c.peerId);
                      setPicked(next);
                    }}
                  />
                </label>
              );
            })
          )}
        </div>
      </Modal>

      {/* 邀请成员入群（v3.9） */}
      <Modal
        open={inviteModalOpen}
        onOpenChange={setInviteModalOpen}
        title="邀请成员"
        description="勾选好友加入群聊"
        footer={
          <Button
            size="sm"
            loading={groupBusy}
            disabled={picked.size === 0}
            onClick={() => void inviteGroup()}
          >
            邀请
          </Button>
        }
      >
        <div className="space-y-1">
          {sortedContacts.filter((c) => !activeGroup?.members.includes(c.peerId)).length === 0 ? (
            <p className="text-xs text-dim">好友都已在群里了</p>
          ) : (
            sortedContacts
              .filter((c) => !activeGroup?.members.includes(c.peerId))
              .map((c) => {
                const on = picked.has(c.peerId);
                return (
                  <label
                    key={c.peerId}
                    className="flex cursor-pointer items-center justify-between rounded-btn px-2 py-1.5 hover:bg-panel-2"
                  >
                    <span className="min-w-0 truncate text-sm text-text">
                      <span
                        className={`mr-1.5 inline-block h-2 w-2 rounded-full align-middle ${
                          onlineAt.has(c.peerId) ? 'bg-ok' : 'bg-dim'
                        }`}
                      />
                      {c.name || shortId(c.peerId, 8, 4)}
                    </span>
                    <input
                      type="checkbox"
                      className="accent-[var(--accent)]"
                      checked={on}
                      onChange={() => {
                        const next = new Set(picked);
                        if (on) next.delete(c.peerId);
                        else next.add(c.peerId);
                        setPicked(next);
                      }}
                    />
                  </label>
                );
              })
          )}
        </div>
      </Modal>
    </div>
  );
}

export default ChatListPage;
